import { FC, useMemo } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiButton, MiPlaceholderArea } from "@miplatform/components";
import { ButtonVariants, CalendarType } from "@miplatform/types";
import { sortByDisplayOrder } from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { CalendarTabCard } from "./CalendarTabCard";

export const CalendarTabListView: FC<CalendarTabListViewProps> = ({
  form,
  extraTabProps: { isReadOnly, onTabChange, onAddTab } = {},
}: CalendarTabListViewProps) => {
  const { classes, theme } = useStyles();
  const { setValue, watch, clearErrors } = form;
  const calendarConfigs = watch("calendarConfigs");
  const sortedCalendarConfigs = useMemo(
    () => sortByDisplayOrder(calendarConfigs),
    [calendarConfigs]
  );
  const filteredCalendarConfigs = useMemo(
    () =>
      sortedCalendarConfigs.filter(
        (config) => config.calendarType === CalendarType.HoldsList
      ),
    [sortedCalendarConfigs]
  );

  const swapDisplayOrders = (tabAIndex: number, tabBIndex: number) => {
    const newCalendarConfigs = [...sortedCalendarConfigs];
    const tempDisplayOrder = newCalendarConfigs[tabAIndex].displayOrder;
    newCalendarConfigs[tabAIndex].displayOrder =
      newCalendarConfigs[tabBIndex].displayOrder;
    newCalendarConfigs[tabBIndex].displayOrder = tempDisplayOrder;

    return newCalendarConfigs;
  };

  const handleReorderUp = (configId: string) => {
    const index = sortedCalendarConfigs.findIndex(
      (config) => config.id === configId
    );

    if (index !== -1 && index > 0) {
      const newCalendarConfigs = swapDisplayOrders(index, index - 1);

      setValue("calendarConfigs", newCalendarConfigs, { shouldDirty: true });
    }
  };

  const handleReorderDown = (configId: string) => {
    const index = sortedCalendarConfigs.findIndex(
      (config) => config.id === configId
    );

    if (index !== -1 && index < sortedCalendarConfigs.length - 1) {
      const newCalendarConfigs = swapDisplayOrders(index, index + 1);

      setValue("calendarConfigs", newCalendarConfigs, { shouldDirty: true });
    }
  };

  const handleDeleteTab = (configId: string) => {
    const index = calendarConfigs.findIndex((config) => config.id === configId);

    if (index === -1) {
      return;
    }

    clearErrors(`calendarConfigs.${index}`);
    setValue(
      "calendarConfigs",
      calendarConfigs.filter((config) => config.id !== configId),
      { shouldDirty: true }
    );
  };

  const handleEditTab = (configId: string) => {
    onTabChange?.(configId);
  };

  return (
    <ConfigAccordionHeader title="Calendar Tabs">
      {filteredCalendarConfigs.length === 0 ? (
        <MiPlaceholderArea
          text="No Tabs Created"
          helperText="Tip: Create your tabs and you can see them here."
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            height: "100%",
            minHeight: 450,
          }}
          buttons={
            !isReadOnly
              ? [
                  {
                    title: "Add Tab",
                    startIcon: mdiPlus,
                    variant: ButtonVariants.Contained,
                    onClick: onAddTab,
                  },
                ]
              : []
          }
        />
      ) : (
        <>
          <Flipper
            flipKey={filteredCalendarConfigs
              .map((config) => config.id)
              .join("-")}
            className={classes.list}
          >
            {filteredCalendarConfigs.map((config, index) => (
              <Flipped key={config.id} flipId={config.id}>
                {(flipppedProps) => (
                  <div {...flipppedProps}>
                    <CalendarTabCard
                      config={config}
                      isReadOnly={isReadOnly}
                      onDeleteClick={handleDeleteTab}
                      onEditClick={handleEditTab}
                      onReorderUp={handleReorderUp}
                      onReorderDown={handleReorderDown}
                      isFirstInList={index === 0}
                      isLastInList={
                        filteredCalendarConfigs.length - 1 === index
                      }
                    />
                  </div>
                )}
              </Flipped>
            ))}
          </Flipper>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            {!isReadOnly && (
              <MiButton
                startIcon={{ path: mdiPlus }}
                variant={ButtonVariants.Contained}
                onClick={onAddTab}
                title="Add Tab"
              />
            )}
          </Box>
        </>
      )}
    </ConfigAccordionHeader>
  );
};

interface CalendarTabListViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    onAddTab?: () => void;
    onTabChange?: (id: string) => void;
  };
}

const useStyles = makeStyles()((theme) => ({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));
